import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchAppointmentDetailsTimeslots } from "../../../../shared/hooks/useFetchAppointment";
import API_URLS from "../../Utils/axr";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";
import store from "../store";
import DwLogutil from "../../Utils/DwLogutil";
import { errorConditionGTM } from "../../Utils/googleTags";
import { v4 as uuidv4 } from "uuid";

type appointmentTimeslotStateType = {
  appointmentTimeslotData: any;
  loading: boolean;
  error: any;
};

const initialState: appointmentTimeslotStateType = {
  appointmentTimeslotData: null,
  loading: false,
  error: null,
};

export const getAppointmentTimeslots = createAsyncThunk(
  "appointment/timeslots",
  async (_, { rejectWithValue }) => {
    let changeFlowScheduleAppointment = false;
    try {
      const reduxStore = store.getState();

      const headers = reduxStore?.brightspeedToken?.headers;
      const user = reduxStore?.user?.data;
      const addressQualification = reduxStore?.addressQualification?.data;
      changeFlowScheduleAppointment = reduxStore?.getAccordionStateSlice?.scheduleAppointment;
      const serviceAdd = user?.addresses?.find(
        (addr: any) => addr?.addressType === "ServiceAddress"
      );

      const { lookup } = await import("zipcode-to-timezone");
      const lookupRes = lookup(serviceAdd?.zip);

      const { format, addDays } = await import("date-fns");

      const pendingDisconnectDate =
        reduxStore?.upComingAppointment?.upcomingAppointment?.upcomingAppointments?.[0]
          ?.orderDetails?.pendingDisconnectDate;

      const getNextDayFromPendingDisconnect = (pendingDisconnectDate: any) => {
        const today = new Date();
        let baseDate;

        if (pendingDisconnectDate) {
          const disconnectDate = new Date(pendingDisconnectDate);
          baseDate = disconnectDate > today ? disconnectDate : today;
        } else {
          baseDate = today;
        }

        return addDays(baseDate, 1);
      };

      const startDate = format(
        getNextDayFromPendingDisconnect(pendingDisconnectDate),
        "yyyy-MM-dd"
      );
      const endDate = format(
        addDays(getNextDayFromPendingDisconnect(pendingDisconnectDate), 60),
        "yyyy-MM-dd"
      );

      const payload = {
        baseline: {
          transactionId:
            addressQualification?.addressQualification?.entity?.partnerSessionId ||
            uuidv4().slice(0, 21),
          channel: "web-acm",
          wirecenter:
            addressQualification?.addressQualification?.entity?.wireCenter ||
            "MANTNCXA",
          siteId: headers?.siteId?.toLowerCase(),
          // workflow: "migratecoppertofiber",
        },
        jobLocation: {
          street: serviceAdd?.street,
          city: serviceAdd?.city,
          state: serviceAdd?.state,
          zipCode: serviceAdd?.zip,
          country:
            addressQualification?.addressQualification?.entity?.country || "USA",
          geoLocation: {
            longitude:
              addressQualification?.addressQualification?.entity?.longitude,
            latitude:
              addressQualification?.addressQualification?.entity?.latitude,
          },
        },
        customerInfo: {
          customerName: `${user?.firstName || ""} ${user?.lastName || ""}`,
          phoneNumber: user?.contactNumber || "",
          timeZone:
            lookupRes === "America/Indiana/Indianapolis"
              ? "America/New_York"
              : lookupRes === "America/Detroit"
              ? "America/New_York"
              : lookupRes || "America/New_York",
        },
        appointment: {
          searchWindowStart: startDate + "T00:00:00",
          searchWindowEnd: endDate + "T00:00:00",
        },
      };

      const params = { "": API_URLS?.appointmentDetailsUrlForCTL };

      DwLogutil({stepName: changeFlowScheduleAppointment ? EC_DW_LOGGING_STEPS.available_slot_api_triggered?.replace(/^SM\s*-*/i, "Changeflow"): EC_DW_LOGGING_STEPS.available_slot_api_triggered});

      const response = await fetchAppointmentDetailsTimeslots(
        params,
        payload,
        headers
      );

      DwLogutil({
        stepName: response?.data?.messageStatus[0]?.statusCode == 200
          ?  changeFlowScheduleAppointment ? EC_DW_LOGGING_STEPS.available_slot_api_success?.replace(/^SM\s*-*/i, "Changeflow"):
              EC_DW_LOGGING_STEPS.available_slot_api_success
          : changeFlowScheduleAppointment ? EC_DW_LOGGING_STEPS.available_slot_api_failed?.replace(/^SM\s*-*/i, "Changeflow"):
            EC_DW_LOGGING_STEPS.available_slot_api_failed,
        error:
          response?.data?.length === 0 ? "Response data is empty" : undefined,
      });

      return response?.data || response;
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        "Something went wrong while fetching appointment timeslots";

      DwLogutil({
        stepName: changeFlowScheduleAppointment ? EC_DW_LOGGING_STEPS.available_slot_api_failed?.replace(/^SM\s*-*/i, "Changeflow"): EC_DW_LOGGING_STEPS.available_slot_api_failed,
        error: errorMessage,
      });

      return rejectWithValue(errorMessage);
    }
  }
);


const appointmentTimeslotSlice = createSlice({
  name: "appointmentTimeslot",
  initialState,
  reducers: {
    clearAppointmentTimeslotSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getAppointmentTimeslots.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAppointmentTimeslots.fulfilled, (state, action) => {
      state.loading = false;
      state.appointmentTimeslotData = action.payload;
      state.error = null;
    });
    builder.addCase(getAppointmentTimeslots.rejected, (state, action) => {
      state.loading = false;
      state.appointmentTimeslotData = null;
      state.error = action.payload;

      errorConditionGTM(
        "BRSPD_Fiber_EC_Flow",
        "appointment timeslots",
        action.error?.message,
        action?.type
      );
    });
  },
});

export const { clearAppointmentTimeslotSlice } =
  appointmentTimeslotSlice.actions;

export default appointmentTimeslotSlice.reducer;
